import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Save, ArrowLeft, User, Mail, Loader2 } from 'lucide-react';
import { getApiUrl } from '../utils/api';

export function UserCreatePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'CLIENT',
    status: 'ACTIVE',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorDetails([]);
    if (!formData.email.trim()) {
      setError('L\'email est obligatoire.');
      return;
    }
    if (formData.password && formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/users'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password || undefined,
          userType: formData.userType,
          status: formData.status,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (data.success) {
        navigate('/users');
        return;
      }
      setError(data.message || 'Erreur lors de la création de l\'utilisateur.');
      if (Array.isArray(data.errors)) {
        setErrorDetails(data.errors.map((err: any) => typeof err === 'string' ? err : err.message || JSON.stringify(err)));
      }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link to="/users" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <User className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Créer un utilisateur</h1>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
              {errorDetails.length > 0 && (
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {errorDetails.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  required
                  autoComplete="off"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="input-field pl-10 w-full"
                  placeholder="utilisateur@exemple.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe (optionnel)</label>
              <input
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                className="input-field w-full"
                placeholder="Min. 8 caractères"
                minLength={8}
              />
              <p className="mt-1 text-xs text-gray-500">Si vide, l'utilisateur pourra définir un mot de passe plus tard (réinitialisation).</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de compte</label>
              <select
                value={formData.userType}
                onChange={(e) => setFormData((prev) => ({ ...prev, userType: e.target.value }))}
                className="input-field w-full"
              >
                <option value="CLIENT">Client</option>
                <option value="ADMIN">Admin</option>
                <option value="PRACTITIONER">Praticien</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                className="input-field w-full"
              >
                <option value="ACTIVE">Actif</option>
                <option value="INACTIVE">Inactif</option>
                <option value="SUSPENDED">Suspendu</option>
                <option value="LOCKED">Verrouillé</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {loading ? 'Création...' : 'Créer'}
            </button>
            <Link to="/users" className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Annuler
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
